import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useNavigate } from 'react-router-dom';
import { relativeFromIso } from '@/utils/format';
import type { ActivityFeedEntry } from '@/domain/types';
import { X, ChevronRight } from 'lucide-react';

function destinationFor(entry: ActivityFeedEntry): string | null {
  // Type-specific routing first.
  switch (entry.type) {
    case 'ai-handoff':
    case 'ai-handoff-resolved':
      return '/ai-conversations';
    case 'ai-voice-handoff':
      return entry.customerId ? `/customer/${entry.customerId}` : null;
    case 'reactivation-warm-handoff':
      return '/reactivation';
    case 'future-readiness-captured':
    case 'future-readiness-fired':
      return entry.customerId ? `/customer/${entry.customerId}` : '/future-readiness';
    case 'self-book-link-sent':
    case 'self-book-link-used':
      return entry.customerId ? `/customer/${entry.customerId}` : null;
    case 'cross-studio-route':
      return entry.customerId ? `/customer/${entry.customerId}` : null;
    case 'booking-created':
    case 'booking-cancelled':
    case 'booking-checked-in':
    case 'booking-seated':
    case 'booking-session-noted':
    case 'booking-completed':
      return entry.customerId
        ? `/customer/${entry.customerId}`
        : entry.studioId
          ? `/calendar/${entry.studioId}`
          : null;
    default:
      break;
  }
  // Generic fallbacks.
  if (entry.customerId) return `/customer/${entry.customerId}`;
  if (entry.studioId) return `/studio/${entry.studioId}`;
  return null;
}

export default function ActivityFeed() {
  const navigate = useNavigate();
  const feed = useBrainStore((s) => s.activityFeed);
  const toggle = useUIStore((s) => s.toggleActivityFeed);

  // Track which entry ids the feed has already shown so newly arrived ones get
  // a brief gold-glow flash on mount. Entries land at index 0 (newest-first).
  const seenIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const e of feed) seenIdsRef.current.add(e.id);
  }, [feed]);

  return (
    <aside
      data-testid="activity-feed"
      className="w-80 shrink-0 border-l border-gold-dim/15 bg-ink-deep flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gold-dim/15">
        <h3 className="label-small-caps text-gold-bright">Activity</h3>
        <button onClick={toggle} className="btn-quiet" aria-label="Collapse activity feed">
          <X size={14} />
        </button>
      </div>
      <ol className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
        {feed.length === 0 ? (
          <li className="font-serif text-sm text-cream-soft px-2">
            Nothing yet. The feed lights up as scripted events fire.
          </li>
        ) : null}
        <AnimatePresence initial={false}>
          {feed.map((entry) => {
            const destination = destinationFor(entry);
            const isFresh = !seenIdsRef.current.has(entry.id);
            return (
              <motion.li
                key={entry.id}
                data-testid={`activity-${entry.id}`}
                initial={
                  isFresh
                    ? { opacity: 0, y: -8, backgroundColor: 'rgba(212,175,55,0.18)' }
                    : false
                }
                animate={{ opacity: 1, y: 0, backgroundColor: 'rgba(0,0,0,0)' }}
                transition={{ duration: 0.6, backgroundColor: { duration: 1.4 } }}
                className="rounded-md"
              >
                <button
                  type="button"
                  disabled={!destination}
                  onClick={() => destination && navigate(destination)}
                  className={`group w-full text-left px-2 py-2 rounded-md transition ${
                    destination
                      ? 'hover:bg-ink-soft/60 cursor-pointer'
                      : 'cursor-default opacity-80'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-inter text-[10px] text-gold-dim uppercase tracking-wider">
                      {relativeFromIso(entry.timestamp)}
                    </span>
                    {destination ? (
                      <ChevronRight
                        size={11}
                        className="text-gold-bright opacity-0 group-hover:opacity-100 transition shrink-0 mt-0.5"
                      />
                    ) : null}
                  </div>
                  <p className="font-serif text-sm text-cream mt-1 leading-snug">{entry.body}</p>
                </button>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </aside>
  );
}
